import { Check, ChevronRight, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@v2/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@v2/components/ui/dropdown-menu";
import { useSeason } from "../../lib/seasons";

/**
 * Selettore della stagione: contesto valido per tutta l'app, stesso ruolo di
 * "Squadra attiva". `onManage` (facoltativo) aggiunge la voce "Gestisci stagioni".
 */
export function SeasonSwitcher({
  onManage,
  variant = "sidebar",
  side = "top",
}: {
  onManage?: () => void;
  variant?: "sidebar" | "compact";
  side?: "top" | "bottom";
}) {
  const { t } = useTranslation("seasons");
  const { seasons, selectedSeason, selectSeason } = useSeason();
  if (!selectedSeason) return null;

  const trigger =
    variant === "compact" ? (
      <button
        type="button"
        className="inline-flex items-center gap-1.5 py-0.5 text-[13px] text-[hsl(var(--faint))] hover:text-foreground transition-colors max-w-full"
      >
        <span className="truncate">{t("switcher.label")} {selectedSeason.name}</span>
        {selectedSeason.status === "closed" && <StatusBadge status="closed" />}
        {seasons.length > 1 && <ChevronsUpDown className="h-3.5 w-3.5 shrink-0" />}
      </button>
    ) : (
      <button
        type="button"
        className="w-full flex items-center gap-2 px-1.5 py-1 rounded-lg text-[13px] text-[hsl(var(--faint))] hover:text-foreground transition-colors text-left"
        title={selectedSeason.name}
      >
        <span className="truncate">{t("switcher.label")} {selectedSeason.name}</span>
        {selectedSeason.status === "closed" && <StatusBadge status="closed" />}
        <ChevronsUpDown className="ml-auto h-3.5 w-3.5 shrink-0" />
      </button>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" side={side} className="w-60">
        {seasons.map((season) => (
          <DropdownMenuItem
            key={season.id}
            onClick={() => selectSeason(season.id)}
            className={cn("flex items-center gap-2", season.id === selectedSeason.id && "bg-accent/30 font-medium")}
          >
            <span className="truncate">{season.name}</span>
            <StatusBadge status={season.status} />
            {season.id === selectedSeason.id && <Check className="ml-auto h-3.5 w-3.5" />}
          </DropdownMenuItem>
        ))}
        {onManage && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onManage} className="flex items-center text-muted-foreground">
              {t("switcher.manage")}
              <ChevronRight className="ml-auto h-3.5 w-3.5" />
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function StatusBadge({ status }: { status: "active" | "closed" }) {
  const { t } = useTranslation("seasons");
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-1.5 py-px font-mono text-[9.5px] leading-tight",
        status === "active"
          ? "bg-accent/15 text-accent dark:bg-accent/25"
          : "bg-muted text-muted-foreground",
      )}
    >
      {status === "active" ? t("status.active") : t("status.closed")}
    </span>
  );
}

/** Stagione selezionata oppure tutte le stagioni (totali di carriera). */
export function SeasonScopeToggle({
  seasonName,
  allSeasons,
  onChange,
}: {
  seasonName: string;
  allSeasons: boolean;
  onChange: (allSeasons: boolean) => void;
}) {
  const { t } = useTranslation("seasons");
  return (
    <div className="inline-flex rounded-full bg-raised p-1 text-sm self-start sm:self-auto max-w-full">
      {[false, true].map((value) => (
        <button
          key={String(value)}
          type="button"
          onClick={() => onChange(value)}
          className={cn(
            "h-9 px-4 rounded-full font-medium transition-colors truncate",
            allSeasons === value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {value ? t("scope.all") : t("scope.season", { name: seasonName })}
        </button>
      ))}
    </div>
  );
}
